import type { BusinessHour } from '@/services/api';
import type { AvailabilityOverride } from '@/services/api';

const DAY_NAMES: Record<string, number> = {
  monday: 1,
  mon: 1,
  tuesday: 2,
  tue: 2,
  tues: 2,
  wednesday: 3,
  wed: 3,
  thursday: 4,
  thu: 4,
  thur: 4,
  thurs: 4,
  friday: 5,
  fri: 5,
  saturday: 6,
  sat: 6,
  sunday: 7,
  sun: 7,
};

const BUSINESS_DAYS = [1, 2, 3, 4, 5, 6, 7] as const;

/** Normalize Supabase time (HH:mm, HH:mm:ss, or ISO datetime) to HH:mm. */
export function normalizeTimeToHHmm(time: string): string {
  if (!time) return '00:00';

  let value = time.trim();
  if (value.includes('T')) {
    value = value.split('T')[1] ?? value;
  }
  value = value.replace(/Z$/i, '').split(/[+-]/)[0] ?? value;

  if (!value.includes(':')) return '00:00';

  const parts = value.split(':');
  const h = parts[0]?.padStart(2, '0') ?? '00';
  const m = (parts[1] ?? '00').padStart(2, '0');
  return `${h}:${m}`;
}

function parseDbBoolean(value: unknown): boolean {
  if (value === true || value === 1 || value === '1') return true;
  if (value === 'true' || value === 't') return true;
  return false;
}

function normalizeTimeValue(value: unknown): string {
  if (typeof value === 'string') return normalizeTimeToHHmm(value);
  if (typeof value === 'number' && Number.isFinite(value)) {
    const hours = Math.floor(value / 60);
    const minutes = value % 60;
    return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;
  }
  return '09:00';
}

/** Map a raw DB weekday to UI convention (1 = Monday … 7 = Sunday). */
export function normalizeBusinessHourDayOfWeek(raw: unknown): number | null {
  if (typeof raw === 'string') {
    const named = DAY_NAMES[raw.trim().toLowerCase()];
    if (named) return named;
  }

  const n = Number(raw);
  if (!Number.isFinite(n)) return null;
  if (n >= 1 && n <= 7) return n;
  // Postgres / JS getDay(): 0 = Sunday, 1 = Monday, …, 6 = Saturday
  if (n >= 0 && n <= 6) return n === 0 ? 7 : n;
  return null;
}

function readRowDay(row: Record<string, unknown>): unknown {
  return (
    row.day_of_week ??
    row.dayOfWeek ??
    row.weekday ??
    row.week_day ??
    row.iso_weekday ??
    row.day_number ??
    row.day ??
    row.dow
  );
}

function readRowClosed(row: Record<string, unknown>): boolean {
  if (row.is_open !== undefined || row.isOpen !== undefined) {
    return !parseDbBoolean(row.is_open ?? row.isOpen);
  }
  return parseDbBoolean(row.is_closed ?? row.isClosed ?? row.closed);
}

/** Normalize a single row from Supabase into a BusinessHour. */
export function normalizeBusinessHourRow(
  row: BusinessHour | Record<string, unknown>,
  agentId: number
): BusinessHour | null {
  const r = row as Record<string, unknown>;
  const day = normalizeBusinessHourDayOfWeek(readRowDay(r));
  if (day == null) return null;

  const startRaw =
    r.start_time ?? r.startTime ?? r.open_time ?? r.openTime ?? r.opens_at;
  const endRaw =
    r.end_time ?? r.endTime ?? r.close_time ?? r.closeTime ?? r.closes_at;

  const idRaw = r.id;
  const parsedId =
    typeof idRaw === 'number'
      ? idRaw
      : typeof idRaw === 'string' && idRaw !== ''
        ? Number(idRaw)
        : undefined;

  return {
    id: parsedId != null && !Number.isNaN(parsedId) ? parsedId : undefined,
    agent_id: agentId,
    day_of_week: day,
    start_time: normalizeTimeValue(startRaw),
    end_time: normalizeTimeValue(endRaw),
    is_closed: readRowClosed(r),
    created_at: typeof r.created_at === 'string' ? r.created_at : undefined,
  };
}

/** Merge fetched rows into one entry per weekday (1=Mon … 7=Sun) with normalized times. */
export function mergeBusinessHours(
  agentId: number,
  hours: BusinessHour[] | Record<string, unknown>[]
): BusinessHour[] {
  const byDay = new Map<number, BusinessHour>();

  for (const row of hours) {
    const normalized = normalizeBusinessHourRow(row, agentId);
    if (normalized) byDay.set(normalized.day_of_week, normalized);
  }

  return BUSINESS_DAYS.map(
    day =>
      byDay.get(day) ?? {
        agent_id: agentId,
        day_of_week: day,
        start_time: '09:00',
        end_time: '17:00',
        is_closed: day >= 6,
      }
  );
}

/** Format time for Supabase time column (HH:mm:00 so it's valid time type) */
export function formatTimeForSupabase(hhmm: string): string {
  const normalized = normalizeTimeToHHmm(hhmm);
  return normalized.length === 5 ? `${normalized}:00` : normalized;
}

export function timeToMinutes(time: string): number {
  const normalized = normalizeTimeToHHmm(time);
  const [h, m] = normalized.split(':').map(Number);
  return (h ?? 0) * 60 + (m ?? 0);
}

/** Smallest HH:mm strictly after startTime, aligned to minuteStep (default 1). */
export function getEarliestEndTimeAfter(
  startTime: string,
  minuteStep = 1
): string {
  const start = timeToMinutes(startTime);
  const next = start + Math.max(1, minuteStep);
  if (next >= 24 * 60) return '23:59';
  const h = Math.floor(next / 60);
  const m = next % 60;
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
}

export function isBusinessHourRangeValid(
  startTime: string,
  endTime: string
): boolean {
  return timeToMinutes(endTime) > timeToMinutes(startTime);
}

/** True when the override date is before today, or today's override window has ended. */
export function isAvailabilityOverridePast(
  override: AvailabilityOverride,
  now: Date = new Date()
): boolean {
  const todayStr = [
    now.getFullYear(),
    String(now.getMonth() + 1).padStart(2, '0'),
    String(now.getDate()).padStart(2, '0'),
  ].join('-');
  const dateStr = normalizeOverrideDate(override.date);
  if (!dateStr) return false;
  if (dateStr < todayStr) return true;
  if (dateStr > todayStr) return false;
  if (override.is_closed) return false;
  const nowMinutes = now.getHours() * 60 + now.getMinutes();
  return nowMinutes >= timeToMinutes(override.end_time);
}

export function normalizeOverrideDate(value: string | Date): string {
  if (value instanceof Date) {
    const y = value.getFullYear();
    const m = String(value.getMonth() + 1).padStart(2, '0');
    const d = String(value.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
  }
  if (typeof value === 'string') {
    return value.slice(0, 10);
  }
  const parsed = new Date(value as unknown as string);
  if (isNaN(parsed.getTime())) return '';
  const y = parsed.getFullYear();
  const m = String(parsed.getMonth() + 1).padStart(2, '0');
  const d = String(parsed.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

export interface EffectiveHours {
  start_time: string;
  end_time: string;
  is_closed: boolean;
}

/**
 * Get effective availability for a date: override for that date if present, otherwise business hours for that weekday.
 */
export function getEffectiveHoursForDate(
  dateStr: string,
  businessHours: BusinessHour[],
  overrides: AvailabilityOverride[]
): EffectiveHours | null {
  const date = new Date(`${dateStr}T00:00:00`);
  if (isNaN(date.getTime())) return null;

  const dateOnly = dateStr.slice(0, 10);
  const override = overrides.find(
    o => normalizeOverrideDate(o.date) === dateOnly
  );

  if (override) {
    return {
      start_time: normalizeTimeToHHmm(override.start_time),
      end_time: normalizeTimeToHHmm(override.end_time),
      is_closed: override.is_closed,
    };
  }

  const jsDay = date.getDay();
  const dayOfWeek = jsDay === 0 ? 7 : jsDay;
  const bh = businessHours.find(h => Number(h.day_of_week) === dayOfWeek);
  if (!bh) return null;

  return {
    start_time: normalizeTimeToHHmm(bh.start_time),
    end_time: normalizeTimeToHHmm(bh.end_time),
    is_closed: bh.is_closed,
  };
}
